import { catalog } from '@nolens/i18n';

export const membersText = catalog(
  {
    onlyTeams: 'Only teams have members.',
    title: (name: string) => `Members of ${name}`,
    needsOwner: 'The team needs at least one owner.',
    noEmail: 'No email',
    you: ' (you)',
    roleOf: (who: string) => `Role of ${who}`,
    member: 'member',
    remove: 'Remove',
    invite: 'Invite',
    inviteHint:
      'Invite by email or create a link to send to whoever you want on the team. Valid for 14 days.',
    inviteEditor: 'Invite as editor',
    inviteEmail: 'Email (optional)',
    inviteEmailHint:
      'We send the invitation link to this address. Without an email, copy the link and send it however you like.',
    inviteEmailPlaceholder: 'name@company.com',
    inviteSent: (email: string) => `Invitation sent to ${email}.`,
    inviteNotSent: (email: string) =>
      `We couldn’t send the email to ${email}. Copy the link below and send it yourself.`,
    inviteViewer: 'Invite as viewer',
    inviteLink: 'Invite link',
    inviteCopied: 'Invite copied.',
    copy: 'Copy',
    inviteFor: (role: string, date: string) =>
      `Invite as ${role.toLowerCase()} · expires on ${date}`,
    revoke: 'Revoke',
    leaveTeam: 'Leave team',
    deleteTeam: 'Delete team',
    deleteTitle: 'Delete the team?',
    leaveTitle: 'Leave the team?',
    deleteHint: 'All the team’s guides and their screenshots are deleted.',
    leaveHint: "You'll no longer see this team’s guides.",
    privacyTitle: 'Recording privacy',
    privacyHint:
      'Rules for the recordings of every team member. Recordings go to the account when they finish (first to the personal space of whoever recorded).',
    reviewRequired: 'Review before sending',
    reviewRequiredHint:
      'When a recording finishes, the extension shows its steps and screenshots so members can delete or blur what shouldn’t go to the account. Without this, reviewing is optional.',
    keepTyped: 'Keep the text typed in fields',
    keepTypedHint:
      'Off: what is typed in fields (names, addresses, numbers) isn’t sent to the account, and is removed from the team’s guides.',
    dropTypedTitle: 'Stop keeping typed text?',
    dropTypedHint:
      'The text typed in fields is also removed from the guides already in the team. This can’t be undone.',
    dropTyped: 'Stop keeping it',
    teamReviews: 'In this team, recordings are reviewed before sending (set by the owner).',
    teamNoTyped: 'In this team, the text typed in fields isn’t kept (set by the owner).',
    cancel: 'Cancel',
    delete: 'Delete',
    leave: 'Leave',
    seatsNote: (monthly: string, yearly: string) =>
      `Each editor beyond 3 adds ${monthly} + VAT per month (${yearly} + VAT per year with annual billing) to the subscription, charged right away in proportion to the period left. Viewers are free.`,
    seatTitle: 'One more billed editor',
    seatHint: (now: number, monthly: string) =>
      `This team has ${now} billed editors. When the person accepts (or right away, when you change the role), the subscription goes to ${now + 1} editors: ${monthly} + VAT more per month, charged now in proportion to the period left.`,
    seatContinue: 'Continue',
  },
  {
    'pt-PT': {
      onlyTeams: 'Só as equipas têm membros.',
      title: (name) => `Membros de ${name}`,
      needsOwner: 'A equipa precisa de pelo menos um dono.',
      noEmail: 'Sem email',
      you: ' (você)',
      roleOf: (who) => `Papel de ${who}`,
      member: 'membro',
      remove: 'Remover',
      invite: 'Convidar',
      inviteHint:
        'Convide por email ou crie uma ligação para enviar a quem quer na equipa. Vale 14 dias.',
      inviteEditor: 'Convite para editor',
      inviteEmail: 'Email (opcional)',
      inviteEmailHint:
        'Enviamos a ligação do convite para este endereço. Sem email, copie a ligação e envie-a como quiser.',
      inviteEmailPlaceholder: 'nome@empresa.pt',
      inviteSent: (email) => `Convite enviado para ${email}.`,
      inviteNotSent: (email) =>
        `Não foi possível enviar o email para ${email}. Copie a ligação abaixo e envie-a.`,
      inviteViewer: 'Convite para leitor',
      inviteLink: 'Ligação de convite',
      inviteCopied: 'Convite copiado.',
      copy: 'Copiar',
      inviteFor: (role, date) => `Convite para ${role.toLowerCase()} · expira a ${date}`,
      revoke: 'Revogar',
      leaveTeam: 'Sair da equipa',
      deleteTeam: 'Apagar equipa',
      deleteTitle: 'Apagar a equipa?',
      leaveTitle: 'Sair da equipa?',
      deleteHint: 'Todos os guias da equipa e as suas capturas são apagados.',
      leaveHint: 'Deixa de ver os guias desta equipa.',
      privacyTitle: 'Privacidade das gravações',
      privacyHint:
        'Regras para as gravações de todos os membros da equipa. As gravações vão para a conta ao concluir (primeiro para o espaço pessoal de quem gravou).',
      reviewRequired: 'Rever antes de enviar',
      reviewRequiredHint:
        'Ao concluir uma gravação, a extensão mostra os passos e as capturas, para os membros apagarem ou desfocarem o que não deve ir para a conta. Sem isto, rever é opcional.',
      keepTyped: 'Guardar o texto escrito nos campos',
      keepTypedHint:
        'Desligado: o que se escreve nos campos (nomes, moradas, números) não segue para a conta e sai dos guias da equipa.',
      dropTypedTitle: 'Deixar de guardar o texto escrito?',
      dropTypedHint:
        'O texto escrito nos campos sai também dos guias que já estão na equipa. Não se pode desfazer.',
      dropTyped: 'Deixar de guardar',
      teamReviews: 'Nesta equipa, as gravações revêem-se antes de enviar (decidido pelo dono).',
      teamNoTyped: 'Nesta equipa, o texto escrito nos campos não é guardado (decidido pelo dono).',
      cancel: 'Cancelar',
      delete: 'Apagar',
      leave: 'Sair',
      seatsNote: (monthly, yearly) =>
        `Cada editor acima de 3 acrescenta ${monthly} + IVA por mês (${yearly} + IVA por ano na faturação anual) à subscrição, cobrado logo na proporção do período que falta. Os leitores são grátis.`,
      seatTitle: 'Mais um editor faturado',
      seatHint: (now, monthly) =>
        `Esta equipa tem ${now} editores faturados. Quando a pessoa aceitar (ou logo, ao mudar o papel), a subscrição passa a ${now + 1} editores: mais ${monthly} + IVA por mês, cobrado já na proporção do período que falta.`,
      seatContinue: 'Continuar',
    },
  },
);
